import React, { useEffect, useState } from 'react';
import { Smartphone, ShieldCheck, AlertTriangle } from 'lucide-react';

// Allowlisted deep-link callbacks (release + debug builds of KAOS AegisNexus).
// Restricting these prevents the session_id from being handed off to an arbitrary app.
const ALLOWED_CALLBACKS = [
  'com.kaosproductions.kaosaegisnexus://auth/callback',
  'com.kaosproductions.kaosaegisnexus.debug://auth/callback',
];
const DEFAULT_CALLBACK = 'com.kaosproductions.kaosaegisnexus://auth/callback';

export default function MobileAuthBridge() {
  const [deepLink, setDeepLink] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cbParam = params.get('cb');
    const cbBase = cbParam ? decodeURIComponent(cbParam) : DEFAULT_CALLBACK;

    if (!ALLOWED_CALLBACKS.includes(cbBase)) {
      setError('Unrecognized app callback. Please update the KAOS AegisNexus app and try again.');
      return;
    }

    const hash = window.location.hash || '';
    if (!hash.includes('session_id=')) {
      setError('No session was returned. Please return to the app and sign in again.');
      return;
    }

    const target = `${cbBase}${hash}`;
    setDeepLink(target);
    // Hand off to the native app.
    window.location.replace(target);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white px-6" data-testid="mobile-auth-bridge">
      <div className="glass-card border border-white/10 rounded-2xl p-10 max-w-md w-full text-center">
        {error ? (
          <>
            <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-7 h-7 text-red-400" />
            </div>
            <h1 className="text-xl font-bold mb-2" data-testid="mobile-auth-error">Sign-in handoff failed</h1>
            <p className="text-gray-400 text-sm">{error}</p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-cyan-500/15 flex items-center justify-center mx-auto mb-5">
              <Smartphone className="w-7 h-7 text-cyan-400" />
            </div>
            <h1 className="text-xl font-bold mb-2">Returning to the app…</h1>
            <p className="text-gray-400 text-sm mb-6 flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-cyan-500" /> Securely handing off your session.
            </p>
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            {deepLink && (
              <a
                href={deepLink}
                data-testid="mobile-auth-open-app-link"
                className="inline-block bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-6 py-3 rounded-xl transition-colors"
              >
                Open KAOS AegisNexus
              </a>
            )}
            <p className="text-gray-600 text-xs mt-4">If the app doesn't open automatically, tap the button above.</p>
          </>
        )}
      </div>
    </div>
  );
}
